import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CubeIcon,
  PuzzlePieceIcon,
  BookmarkIcon,
  Cog6ToothIcon,
  PaintBrushIcon,
  ChartBarIcon,
  ClockIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const Dashboard = ({ config }) => {
  const [isCleaning, setIsCleaning] = useState(false)

  // 清理YAML文件
  const handleCleanupYaml = async () => {
    if (!confirm('确定要清理书签配置中的null值和无效数据吗？此操作不可撤销。')) {
      return
    }

    setIsCleaning(true)
    try {
      const response = await fetch('/api/cleanup-yaml', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: 'bookmarks' }),
      })

      const result = await response.json()

      if (result.success) {
        toast.success(`清理完成！清理了 ${result.originalCount - result.cleanedCount} 个无效条目`)
        // 刷新页面以显示最新数据
        window.location.reload()
      } else {
        toast.error(`清理失败: ${result.error}`)
      }
    } catch (error) {
      toast.error(`清理失败: ${error.message}`)
    } finally {
      setIsCleaning(false)
    }
  }

  // 统计信息
  const stats = {
    services: config?.services ?
      config.services.reduce((total, group) => {
        const groupName = Object.keys(group)[0]
        return total + (group[groupName]?.length || 0)
      }, 0) : 0,
    widgets: config?.widgets?.length || 0,
    bookmarks: config?.bookmarks ?
      config.bookmarks.reduce((total, group) => {
        const groupName = Object.keys(group)[0]
        return total + (group[groupName]?.length || 0)
      }, 0) : 0,
    groups: (config?.services?.length || 0) + (config?.bookmarks?.length || 0),
  }

  const quickActions = [
    {
      name: '配置小部件',
      description: '管理页面上的小部件',
      href: '/widgets',
      icon: PuzzlePieceIcon,
      color: 'bg-green-500',
    },
    {
      name: '添加服务',
      description: '快速添加新的服务到导航页',
      href: '/services',
      icon: CubeIcon,
      color: 'bg-blue-500',
    },
    {
      name: '管理书签',
      description: '添加和组织书签链接',
      href: '/bookmarks',
      icon: BookmarkIcon,
      color: 'bg-yellow-500',
    },
    {
      name: '基础设置',
      description: '配置主题、语言等基本设置',
      href: '/settings',
      icon: Cog6ToothIcon,
      color: 'bg-purple-500',
    },
    {
      name: '自定义样式',
      description: '编辑 CSS 和 JavaScript',
      href: '/styles',
      icon: PaintBrushIcon,
      color: 'bg-pink-500',
    },
  ]

  const statCards = [
    {
      name: '服务总数',
      value: stats.services,
      icon: CubeIcon,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      name: '小部件数量',
      value: stats.widgets,
      icon: PuzzlePieceIcon,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      name: '书签数量',
      value: stats.bookmarks,
      icon: BookmarkIcon,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
    },
    {
      name: '分组总数',
      value: stats.groups,
      icon: ChartBarIcon,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
  ]

  return (
    <div className="space-y-8 fade-in">
      {/* 页面标题 */}
      <div className="page-header">
        <h1 className="page-title">🚀 仪表板</h1>
        <p className="page-subtitle">
          管理你的 Homepage 导航页配置，让一切井然有序
        </p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div
            key={stat.name}
            className="glass-card glow-hover bounce-in"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center">
              <div className={`p-4 rounded-2xl ${stat.bgColor} shadow-lg`}>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400 mb-1">
                  {stat.name}
                </p>
                <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 快速操作 */}
      <div className="slide-up">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-6">
          ⚡ 快速操作
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <Link
              key={action.name}
              to={action.href}
              className="glass-card hover:bg-white/10 transition-all duration-300 group glow-hover transform hover:scale-105"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start">
                <div className={`p-4 rounded-2xl ${action.color} shadow-lg group-hover:shadow-xl transition-all duration-300`}>
                  <action.icon className="h-8 w-8 text-white group-hover:scale-110 transition-transform duration-300" />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-300">
                    {action.name}
                  </h3>
                  <p className="mt-2 text-sm text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                    {action.description}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* 最近活动 */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">系统状态</h2>
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-gray-300">配置文件状态</span>
            </div>
            <span className="badge-success">正常</span>
          </div>
          <div className="mt-4 text-sm text-gray-400">
            所有配置文件已加载，系统运行正常.
          </div>
          <div className="mt-4">
            <button
              onClick={handleCleanupYaml}
              className="btn-secondary btn-sm"
              disabled={isCleaning}
            >
              {isCleaning ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  清理中...
                </>
              ) : (
                <>
                  <TrashIcon className="h-4 w-4 mr-2" />
                  清理YAML文件
                </>
              )}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              清理书签配置中的null值和无效数据
            </p>
          </div>
        </div>
      </div>

      {/* 配置概览 */}
      {config && (
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">配置概览</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 服务组 */}
            <div className="card">
              <h3 className="card-title">服务分组</h3>
              <div className="space-y-2">
                {config.services?.map((group, index) => {
                  const groupName = Object.keys(group)[0]
                  const serviceCount = group[groupName]?.length || 0
                  return (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-b-0">
                      <span className="text-gray-300">{groupName}</span>
                      <span className="text-sm text-gray-400">{serviceCount} 个服务</span>
                    </div>
                  )
                })}
                {(!config.services || config.services.length === 0) && (
                  <p className="text-gray-400 text-sm">暂无服务配置</p>
                )}
              </div>
            </div>

            {/* 书签分组 */}
            <div className="card">
              <h3 className="card-title">书签分组</h3>
              <div className="space-y-2">
                {config.bookmarks?.map((group, index) => {
                  const groupName = Object.keys(group)[0]
                  const bookmarkCount = group[groupName]?.length || 0
                  return (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-b-0">
                      <span className="text-gray-300">{groupName}</span>
                      <span className="text-sm text-gray-400">{bookmarkCount} 个书签</span>
                    </div>
                  )
                })}
                {(!config.bookmarks || config.bookmarks.length === 0) && (
                  <p className="text-gray-400 text-sm">暂无书签配置</p>
                )}
              </div>
            </div>

            {/* 小部件列表 */}
            <div className="card">
              <h3 className="card-title">已配置小部件</h3>
              <div className="space-y-2">
                {config.widgets?.map((widget, index) => {
                  const widgetType = Object.keys(widget)[0]
                  return (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-b-0">
                      <span className="text-gray-300 capitalize">{widgetType}</span>
                      <span className="badge-info">已启用</span>
                    </div>
                  )
                })}
                {(!config.widgets || config.widgets.length === 0) && (
                  <p className="text-gray-400 text-sm">暂无小部件配置</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
